import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { UserPlus, Trash2, ShieldCheck, User } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { PageHeader } from "@/components/PageHeader"
import { useAuth } from "@/contexts/AuthContext"
import { useTipoOperacao } from "@/hooks/useTipoOperacao"
import { supabase } from "@/integrations/supabase/client"
import { getPublicSupabaseFunctionErrorMessage, getSupabaseFunctionErrorMessage } from "@/lib/errors"
import { formatarWhatsApp, isWhatsAppValido, normalizarWhatsApp } from "@/lib/utils"
import { toast } from "sonner"

const perfilSchema = z.object({ nome: z.string().min(2, "Mínimo 2 caracteres") })
const lojaSchema = z.object({
  nome: z.string().min(2, "Mínimo 2 caracteres"),
  whatsapp: z.string().refine(isWhatsAppValido, "Informe um WhatsApp válido com DDD"),
})

type PerfilForm = z.infer<typeof perfilSchema>
type LojaForm = z.infer<typeof lojaSchema>

interface UsuarioLoja {
  id: string
  nome: string | null
  papel: string
  email: string
  created_at: string
}

const Configuracoes = () => {
  const { user, perfil, loja, reload } = useAuth()
  const vertical = useTipoOperacao()
  const isAdmin = perfil?.papel === 'admin'
  const papelLabel = isAdmin ? "Administrador" : "Avaliador"

  // — Perfil
  const [savingPerfil, setSavingPerfil] = useState(false)
  const perfilForm = useForm<PerfilForm>({
    resolver: zodResolver(perfilSchema),
    defaultValues: { nome: perfil?.nome ?? "" },
  })

  // — Loja
  const [savingLoja, setSavingLoja] = useState(false)
  const lojaForm = useForm<LojaForm>({
    resolver: zodResolver(lojaSchema),
    defaultValues: { nome: loja?.nome ?? "", whatsapp: formatarWhatsApp(loja?.whatsapp ?? "") },
  })

  // — Fotos obrigatórias
  const [fotosObrigatorias, setFotosObrigatorias] = useState<boolean>(
    loja?.fotos_entrada_obrigatorias ?? true
  )
  const [savingFotos, setSavingFotos] = useState(false)

  // — Equipe
  const [usuarios, setUsuarios] = useState<UsuarioLoja[]>([])
  const [loadingUsuarios, setLoadingUsuarios] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [inviteForm, setInviteForm] = useState({ email: '', nome: '', papel: 'avaliador' })

  useEffect(() => {
    perfilForm.reset({ nome: perfil?.nome ?? "" })
  }, [perfil?.nome])

  useEffect(() => {
    if (loja?.nome) lojaForm.reset({ nome: loja.nome, whatsapp: formatarWhatsApp(loja.whatsapp ?? "") })
  }, [loja?.nome, loja?.whatsapp])

  useEffect(() => {
    setFotosObrigatorias(loja?.fotos_entrada_obrigatorias ?? true)
  }, [loja?.fotos_entrada_obrigatorias])

  useEffect(() => {
    if (isAdmin) fetchUsuarios()
  }, [isAdmin])

  async function fetchUsuarios() {
    setLoadingUsuarios(true)
    const { data, error } = await supabase.rpc('get_loja_users' as never)
    if (error) { toast.error('Erro ao carregar usuários') }
    setUsuarios(data ?? [])
    setLoadingUsuarios(false)
  }

  async function salvarPerfil(values: PerfilForm) {
    if (!perfil) return
    setSavingPerfil(true)
    const { error } = await supabase.from("perfis").update({ nome: values.nome }).eq("id", perfil.id)
    setSavingPerfil(false)
    if (error) { toast.error("Erro ao salvar perfil"); return }
    await reload()
    toast.success("Perfil atualizado!")
  }

  async function salvarLoja(values: LojaForm) {
    if (!loja) return
    setSavingLoja(true)
    const { error } = await supabase
      .from("lojas")
      .update({ nome: values.nome, whatsapp: normalizarWhatsApp(values.whatsapp) })
      .eq("id", loja.id)
    setSavingLoja(false)
    if (error) { toast.error("Erro ao salvar dados da loja"); return }
    await reload()
    toast.success("Loja atualizada!")
  }

  async function convidar() {
    if (!inviteForm.email || !inviteForm.nome) { toast.error('Preencha email e nome'); return }
    setInviting(true)
    const { error } = await supabase.functions.invoke('invite-user', {
      body: { email: inviteForm.email, nome: inviteForm.nome, papel: inviteForm.papel },
    })
    setInviting(false)
    if (error) {
      console.error("Erro ao convidar usuário:", await getSupabaseFunctionErrorMessage(error, error.message))
      const message = await getPublicSupabaseFunctionErrorMessage(error, "Não foi possível enviar o convite agora.")
      toast.error(message); return
    }
    toast.success(`Convite enviado para ${inviteForm.email}`)
    setInviteForm({ email: '', nome: '', papel: 'avaliador' })
    setDialogOpen(false)
    fetchUsuarios()
  }

  async function alterarPapel(userId: string, novoPapel: string) {
    const { error } = await supabase.from('perfis').update({ papel: novoPapel }).eq('id', userId)
    if (error) { toast.error('Erro ao alterar papel'); return }
    toast.success('Papel atualizado')
    fetchUsuarios()
  }

  async function removerUsuario(userId: string) {
    const { error } = await supabase.from('perfis').delete().eq('id', userId)
    if (error) { toast.error('Erro ao remover usuário'); return }
    toast.success('Usuário removido')
    fetchUsuarios()
  }

  async function salvarFotosObrigatorias(valor: boolean) {
    if (!loja) return
    setSavingFotos(true)
    const { error } = await supabase
      .from("lojas")
      .update({ fotos_entrada_obrigatorias: valor })
      .eq("id", loja.id)
    setSavingFotos(false)
    if (error) { toast.error("Erro ao salvar configuração"); return }
    setFotosObrigatorias(valor)
    await reload()
    toast.success("Configuração salva!")
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Configurações"
        description={`Gerencie perfil, dados da ${vertical.tipo === 'oficina' ? 'oficina' : 'loja'} e acesso da equipe`}
      />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="flex min-h-[104px] flex-col justify-between p-3.5 sm:min-h-[132px] sm:p-5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground sm:text-[11px] sm:tracking-[0.18em]">Perfil</p>
            <p className="mt-2 line-clamp-2 text-base font-semibold leading-tight text-foreground sm:mt-3 sm:text-lg">{perfil?.nome || "Nome não informado"}</p>
            <p className="mt-1 text-xs text-muted-foreground">{user?.email || "Sem email"}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex min-h-[104px] flex-col justify-between p-3.5 sm:min-h-[132px] sm:p-5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground sm:text-[11px] sm:tracking-[0.18em]">Acesso</p>
            <p className="mt-2 text-base font-semibold leading-tight text-foreground sm:mt-3 sm:text-lg">{papelLabel}</p>
            <p className="mt-1 text-xs text-muted-foreground">Nível atual dentro da operação</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex min-h-[104px] flex-col justify-between p-3.5 sm:min-h-[132px] sm:p-5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground sm:text-[11px] sm:tracking-[0.18em]">Loja</p>
            <p className="mt-2 line-clamp-2 text-base font-semibold leading-tight text-foreground sm:mt-3 sm:text-lg">{loja?.nome || "Não vinculada"}</p>
            <p className="mt-1 text-xs text-muted-foreground">Cadastro principal da conta</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex min-h-[104px] flex-col justify-between p-3.5 sm:min-h-[132px] sm:p-5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground sm:text-[11px] sm:tracking-[0.18em]">Equipe</p>
            <p className="font-data mt-2 text-[2rem] font-semibold leading-none text-foreground sm:mt-3 sm:text-3xl">{isAdmin ? usuarios.length : 1}</p>
            <p className="mt-1 text-xs text-muted-foreground">{isAdmin ? "Usuários com acesso" : "Seu acesso atual"}</p>
          </CardContent>
        </Card>
      </div>

      <div className={`grid gap-6 ${isAdmin ? "xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" : "max-w-3xl"}`}>
        <Card>
          <CardHeader>
            <CardTitle>Perfil</CardTitle>
            <CardDescription>Suas informações de acesso e identificação</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={perfilForm.handleSubmit(salvarPerfil)} className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user?.email ?? ""} disabled className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="perfil-nome">Nome</Label>
                <Input id="perfil-nome" {...perfilForm.register("nome")} placeholder="Seu nome" />
                {perfilForm.formState.errors.nome && (
                  <p className="text-xs text-destructive">{perfilForm.formState.errors.nome.message}</p>
                )}
              </div>
              <Button type="submit" disabled={savingPerfil}>
                {savingPerfil ? "Salvando..." : "Salvar perfil"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle>Empresa</CardTitle>
              <CardDescription>
                {`Dados principais da sua ${vertical.tipo === 'oficina' ? 'oficina' : 'loja'} no sistema`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={lojaForm.handleSubmit(salvarLoja)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="loja-nome">
                    {vertical.tipo === 'oficina' ? 'Nome da oficina' : 'Nome da loja'}
                  </Label>
                  <Input
                    id="loja-nome"
                    {...lojaForm.register("nome")}
                    placeholder={vertical.tipo === 'oficina' ? 'Nome da oficina' : 'Nome da loja'}
                  />
                  {lojaForm.formState.errors.nome && (
                    <p className="text-xs text-destructive">{lojaForm.formState.errors.nome.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="loja-whatsapp">WhatsApp</Label>
                  <Input
                    id="loja-whatsapp"
                    inputMode="tel"
                    placeholder="(11) 99999-9999"
                    {...lojaForm.register("whatsapp", {
                      onChange: (event) => {
                        lojaForm.setValue("whatsapp", formatarWhatsApp(event.target.value), {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      },
                    })}
                  />
                  {lojaForm.formState.errors.whatsapp && (
                    <p className="text-xs text-destructive">{lojaForm.formState.errors.whatsapp.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Tipo de operação</Label>
                  <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
                    <span className="font-medium text-foreground">{vertical.rotulo}</span>
                    <span className="text-xs text-muted-foreground">
                      definido no cadastro · entre em contato com o suporte para alterar
                    </span>
                  </div>
                </div>
                <Button type="submit" disabled={savingLoja}>
                  {savingLoja ? "Salvando..." : "Salvar"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>

      {vertical.tipo === 'oficina' && isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>Configurações da Entrada</CardTitle>
            <CardDescription>
              Defina como os funcionários devem preencher as entradas de veículos.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="fotos-obrigatorias" className="text-sm font-medium">
                  Fotos obrigatórias em todas as etapas
                </Label>
                <p className="text-sm text-muted-foreground">
                  Quando ativado, o funcionário não pode finalizar a entrada sem fotografar cada etapa.
                </p>
              </div>
              <Switch
                id="fotos-obrigatorias"
                checked={fotosObrigatorias}
                disabled={savingFotos}
                onCheckedChange={(val) => void salvarFotosObrigatorias(val)}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {isAdmin && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between space-y-0">
            <div>
              <CardTitle>Equipe</CardTitle>
              <CardDescription className="mt-1">
                {usuarios.length} membro{usuarios.length !== 1 ? 's' : ''}
              </CardDescription>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Convidar
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Convidar usuário</DialogTitle>
                  <DialogDescription>
                    O usuário receberá um email para criar sua senha e acessar a loja.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label>Nome</Label>
                    <Input
                      placeholder="Nome completo"
                      value={inviteForm.nome}
                      onChange={e => setInviteForm(f => ({ ...f, nome: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      placeholder="email@exemplo.com"
                      value={inviteForm.email}
                      onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Papel</Label>
                    <Select value={inviteForm.papel} onValueChange={v => setInviteForm(f => ({ ...f, papel: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="avaliador">Avaliador</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button onClick={convidar} disabled={inviting}>
                    {inviting ? 'Enviando...' : 'Enviar convite'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {loadingUsuarios ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Carregando...</p>
            ) : (
              <div className="space-y-2">
                {usuarios.map(u => (
                  <div key={u.id} className="flex items-center gap-3 rounded-[calc(var(--radius)-0.05rem)] border border-border/70 bg-white/70 p-3">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-sm font-medium text-primary">
                        {(u.nome ?? u.email)[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{u.nome ?? '—'}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {u.id !== user?.id ? (
                        <Select value={u.papel} onValueChange={v => alterarPapel(u.id, v)}>
                          <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="avaliador">Avaliador</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant={u.papel === 'admin' ? 'default' : 'secondary'} className="text-xs">
                          {u.papel === 'admin'
                            ? <><ShieldCheck className="h-3 w-3 mr-1" />Admin</>
                            : <><User className="h-3 w-3 mr-1" />Avaliador</>}
                        </Badge>
                      )}
                      {u.id !== user?.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remover usuário?</AlertDialogTitle>
                              <AlertDialogDescription>
                                {u.nome ?? u.email} perderá o acesso à loja.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive hover:bg-destructive/90"
                                onClick={() => removerUsuario(u.id)}
                              >
                                Remover
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default Configuracoes
